// hooks/useFollowUser.js
import { useState, useEffect } from "react";
import {
  getDoc,
  doc,
  collection,
  setDoc,
  deleteDoc,
  serverTimestamp,
  updateDoc,
  increment,
} from "firebase/firestore";
import { db } from "../FirebaseConfig";
import useNotification from "./useNotification";
import useFeedNotification from "./useFeedNotification";
import { toast } from "react-toastify";
import { toastOptions } from "../lib/toastOptions";
import useFeedClient from "../StatesStore/FeedClient";
import { usePointsSystem } from "../Points/usePointsSystem";

const useFollowUser = (currentUserId, profileUserId) => {
  const [isFollowed, setIsFollowed] = useState(false);
  const [isFollowBack, setIsFollowBack] = useState(false);
  const [loading, setLoading] = useState(true);
  const { addNotification } = useNotification();
  const { addFeedNotification } = useFeedNotification();
  const { awardPoints } = usePointsSystem(currentUserId);

  const checkIfFollowing = async () => {
    // Check if current user is following the profile user
    const followingDocRef = doc(
      db,
      "users",
      currentUserId,
      "following",
      profileUserId
    );
    const followingSnapshot = await getDoc(followingDocRef);
    if (followingSnapshot.exists()) {
      setIsFollowed(true);
    }
  };

  const checkIfFollowBack = async () => {
    // Check if profile user is following the current user (for follow-back logic)
    const followersDocRef = doc(
      db,
      "users",
      currentUserId,
      "followers",
      profileUserId
    );
    const followersSnapshot = await getDoc(followersDocRef);
    if (followersSnapshot.exists()) {
      setIsFollowBack(true);
    }
  };
  useEffect(() => {
    const checkFollowStatus = async () => {
      if (!currentUserId || !profileUserId) return;
      await checkIfFollowing();
      await checkIfFollowBack();
      setLoading(false);
    };

    checkFollowStatus();
  }, [currentUserId, profileUserId]);

  const followUser = async () => {
    if (!currentUserId || !profileUserId) return;
    setLoading(true);
    try {
      // Add profileUserId to current user's following collection
      const followingRef = doc(
        db,
        "users",
        currentUserId,
        "following",
        profileUserId
      );
      await setDoc(followingRef, {
        type: "user",
        timestamp: serverTimestamp(),
      });

      // Add currentUserId to profile user's followers collection
      const followerRef = doc(
        db,
        "users",
        profileUserId,
        "followers",
        currentUserId
      );
      await setDoc(followerRef, { type: "user", timestamp: serverTimestamp() });

      // Set state to reflect follow status
      setIsFollowed(true);
      setIsFollowBack(false);
      // Update following count for current user
      const currentUserRef = doc(db, "users", currentUserId);
      await updateDoc(currentUserRef, {
        followingCount: increment(1),
      });

      // Update followers count for profile user
      const profileUserRef = doc(db, "users", profileUserId);
      await updateDoc(profileUserRef, {
        followerCount: increment(1),
      });
      // API request to GetStream to follow the user
      await fetch(`${process.env.REACT_APP_API}/api/followUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          senderId: currentUserId,
          receiverId: profileUserId,
        }),
      });

      // Add notifications
      if (currentUserId !== profileUserId) {
        awardPoints("followUser");
        await addNotification({
          userId: profileUserId,
          id: currentUserId,
          type: "followed",
          timestamp: serverTimestamp(),
          by: "user",
          seen: false,
        });

        addFeedNotification({
          ownerId: profileUserId,
          id: currentUserId,
          type: "followedUser",
          by: "user",
        });
      }

      toast.success("User followed successfully", toastOptions);
    } catch (error) {
      console.error("Error following user:", error);
      toast.error("Failed to follow user", toastOptions);
    } finally {
      setLoading(false);
    }
  };

  const unfollowUser = async () => {
    if (!currentUserId || !profileUserId) return;
    setLoading(true);
    try {
      await checkIfFollowBack();
      setIsFollowed(false);
      toast.success("User unfollowed successfully", toastOptions);

      await fetch(`${process.env.REACT_APP_API}/api/unfollowUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          senderId: currentUserId,
          receiverId: profileUserId,
        }),
      });
      // Set state to reflect unfollow status

      // Update following count for current user
      const currentUserRef = doc(db, "users", currentUserId);
      const currentUserDoc = await getDoc(currentUserRef);
      if (currentUserDoc.data().followingCount > 0) {
        await updateDoc(currentUserRef, {
          followingCount: increment(-1),
        });
      }

      // Update followers count for profile user
      const profileUserRef = doc(db, "users", profileUserId);
      const profileUserDoc = await getDoc(profileUserRef);
      if (profileUserDoc.data().followerCount > 0) {
        await updateDoc(profileUserRef, {
          followerCount: increment(-1),
        });
      }
    } catch (error) {
      console.error("Error unfollowing user:", error);
      toast.error("Failed to unfollow user", toastOptions);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    isFollowed,
    setIsFollowed,
    isFollowBack,
    followUser,
    unfollowUser,
  };
};

export default useFollowUser;
